/**
 * cc-usage トークン集計方法 (token metric) のヘルパー。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage dashboard で表示するトークン数の集計方法を切り替える共通ロジック。
 *   一日 / プロジェクト単位の作業量を計る指標としては、cache を含めた合計より Input + Output のみの
 *   ほうが有用なケースがあるため、閲覧者が集計方法を選べるようにする
 * @graph-connects none
 */

import type { CcUsageTokens } from '../../../types/api.js';

/**
 * トークン集計方法。
 *
 * - total:       Input + Output + Cache 作成 + Cache 読込 の全 4 種を合計する
 * - inputOutput: Input + Output のみを合計する (cache を除外)。
 *   cache はその時点の作業ではなくセッションのコンテキストに依存して大きく変動するため、
 *   作業量の指標としては cache 抜きのほうが実態を表しやすい
 *
 * @graph-connects none
 */
export type CcUsageTokenMetric = 'total' | 'inputOutput';

/**
 * トークン種別キー。CcUsageTokens のフィールド名に一致する。
 * @graph-connects none
 */
export type CcUsageTokenType = keyof CcUsageTokens;

/**
 * トークン集計方法ごとに、合計へ含めるトークン種別。
 * 内訳バー・凡例・積み上げチャートの表示順もこの配列順に従う。
 * @graph-connects none
 */
const TOKEN_TYPES_BY_METRIC: Record<CcUsageTokenMetric, readonly CcUsageTokenType[]> = {
  total: ['inputTokens', 'outputTokens', 'cacheCreationInputTokens', 'cacheReadInputTokens'],
  inputOutput: ['inputTokens', 'outputTokens'],
};

/**
 * 指定したトークン集計方法で集計対象となるトークン種別の一覧を返す。
 * @graph-connects none
 */
export function tokenTypesForMetric(metric: CcUsageTokenMetric): readonly CcUsageTokenType[] {
  return TOKEN_TYPES_BY_METRIC[metric];
}

/**
 * トークン内訳を、指定したトークン集計方法で 1 つの数値に合計する。
 * @graph-connects none
 */
export function tokenAmount(tokens: CcUsageTokens, metric: CcUsageTokenMetric): number {
  return tokenTypesForMetric(metric).reduce((sum, type) => sum + tokens[type], 0);
}
